import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Drawer, Modal } from 'antd';
import dayjs from 'dayjs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faPhone, faUpRightFromSquare, faXmark } from '@fortawesome/free-solid-svg-icons';
import {
  CallDrawer,
  DangerLink,
  DrawerBody,
  DrawerClose,
  DrawerHeadRow,
  DrawerHeader,
  DrawerStats,
  DrawerSubtitle,
  DrawerTitle,
  Field,
  FieldLabel,
  Form,
  FormField,
  FormRow,
  GhostBtn,
  PillRow,
  PrimaryBtn,
  SearchSelect,
  Section,
  SectionText,
  Select,
  StatusPill,
  TextArea
} from '../../components';
import { useAuth } from '../../context/AuthContext';
import { useAddTicketNote, useDeleteTicket, useUpdateTicket } from '../../queries/maintenance';
import { useVendorOptions } from '../../queries/vendors';
import { P } from '../../theme/tokens';
import { ageOf } from '../../utils/maintenance';
import { STATUSES, TIER_OPTIONS, TONE, statusValue } from '../../utils/maintenance';

const BODY_STYLE = { padding: 0, display: 'flex', flexDirection: 'column', background: P.bgSubtle };
const AGE_TONE = { default: P.subtle, info: P.info, warning: P.warning, danger: P.danger };


function WorkOrder({ ticket, canManage }) {
  const update = useUpdateTicket();
  const { data: vendors = [] } = useVendorOptions();
  const save = (patch) => update.mutate({ id: ticket.id, ...patch });

  const setStatus = (label) =>
    save(label === 'Unassigned' ? { status: 'new', vendorId: '', assignedTo: '' } : { status: statusValue(label) });

  const setVendor = (vendorId) =>
    save({ vendorId, assignedTo: vendors.find((v) => v._id === vendorId)?.company || '' });

  const vendorKnown = !ticket.vendorId || vendors.some((v) => v._id === ticket.vendorId);

  return (
    <Section>
      <h3>Work order</h3>
      <Form as="fieldset" disabled={!canManage} style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}>
        <FormField>
          <FieldLabel htmlFor="ticket-issue">Issue</FieldLabel>
          <Field
            key={`issue-${ticket.issue}`}
            id="ticket-issue"
            defaultValue={ticket.issue}
            onBlur={(e) => {
              const issue = e.target.value.trim();
              if (issue && issue !== ticket.issue) save({ issue });
            }}
          />
        </FormField>
        <FormField>
          <FieldLabel htmlFor="ticket-property">Property / unit</FieldLabel>
          <Field
            key={`property-${ticket.property}`}
            id="ticket-property"
            defaultValue={ticket.property}
            onBlur={(e) => {
              const propertyUnit = e.target.value.trim();
              if (propertyUnit !== ticket.property) save({ propertyUnit });
            }}
          />
        </FormField>
        <FormRow>
          <FormField>
            <FieldLabel htmlFor="ticket-status">Status</FieldLabel>
            <Select id="ticket-status" style={{ minWidth: 0 }} value={ticket.status} onChange={(e) => setStatus(e.target.value)}>
              {STATUSES.map((s) => (
                <option key={s.label} value={s.label}>
                  {s.label}
                </option>
              ))}
            </Select>
          </FormField>
          <FormField>
            <FieldLabel htmlFor="ticket-tier">Tier</FieldLabel>
            <Select id="ticket-tier" style={{ minWidth: 0 }} value={ticket.tierKey} onChange={(e) => save({ tier: e.target.value })}>
              {TIER_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
              {ticket.tierKey === 'water' && <option value="water">Urgent</option>}
            </Select>
          </FormField>
        </FormRow>
        <FormField>
          <FieldLabel htmlFor="ticket-vendor">Assigned vendor</FieldLabel>
          <SearchSelect
            id="ticket-vendor"
            allowClear
            placeholder={ticket.assigned && !ticket.vendorId ? ticket.assigned : 'Unassigned'}
            value={ticket.vendorId}
            onChange={setVendor}
            options={[
              ...(vendorKnown ? [] : [{ value: ticket.vendorId, label: ticket.assigned }]),
              ...vendors.map((v) => ({ value: v._id, label: v.trade ? `${v.company} (${v.trade})` : v.company }))
            ]}
          />
        </FormField>
        <FormField>
          <FieldLabel>Scheduling</FieldLabel>
          <span style={{ color: P.danger }}>Maintenance scheduling is coming soon.</span>
        </FormField>
      </Form>
    </Section>
  );
}

function OwnerApproval({ ticket, canManage }) {
  const approval = ticket.ownerApproval || {};
  const update = useUpdateTicket();
  const [form, setForm] = useState({
    amount: approval.amount ?? '',
    ownerName: approval.ownerName || '',
    requestedAt: approval.requestedAt ? dayjs(approval.requestedAt).format('YYYY-MM-DD') : ''
  });
  const has = approval.amount != null || approval.ownerName;
  if (!has && !canManage) return null;
  const waiting = approval.requestedAt ? `, waiting ${dayjs().diff(approval.requestedAt, 'day')}d` : '';
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const save = (e) => {
    e.preventDefault();
    update.mutate({
      id: ticket.id,
      ownerApproval: {
        amount: form.amount === '' ? null : Number(form.amount),
        ownerName: form.ownerName.trim(),
        requestedAt: form.requestedAt ? new Date(form.requestedAt).toISOString() : null
      }
    });
  };

  return (
    <Section>
      <h3>Owner approval</h3>
      {has && (
        <SectionText style={{ marginTop: 0 }}>
          {[approval.amount != null && `$${Number(approval.amount).toLocaleString('en-US')} quote`, approval.ownerName].filter(Boolean).join(' · ')}
          {waiting}
        </SectionText>
      )}
      {canManage && (
        <form onSubmit={save} style={{ display: 'grid', gap: 8, marginTop: 8 }}>
          <FormRow>
            <FormField>
              <FieldLabel htmlFor="approval-amount">Quote ($)</FieldLabel>
              <Field id="approval-amount" type="number" min="0" value={form.amount} onChange={set('amount')} />
            </FormField>
            <FormField>
              <FieldLabel htmlFor="approval-requested">Requested on</FieldLabel>
              <Field id="approval-requested" type="date" value={form.requestedAt} onChange={set('requestedAt')} />
            </FormField>
          </FormRow>
          <FormField>
            <FieldLabel htmlFor="approval-owner">Owner</FieldLabel>
            <Field id="approval-owner" value={form.ownerName} onChange={set('ownerName')} placeholder="Who approves the quote" />
          </FormField>
          <GhostBtn type="submit" disabled={update.isPending} style={{ justifyContent: 'center' }}>
            Save approval
          </GhostBtn>
        </form>
      )}
    </Section>
  );
}

function Notes({ ticket, canManage }) {
  const [text, setText] = useState('');
  const add = useAddTicketNote();
  const submit = (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    add.mutate({ id: ticket.id, text: text.trim() }, { onSuccess: () => setText('') });
  };

  return (
    <Section>
      <h3>{`Notes (${ticket.notes.length})`}</h3>
      {ticket.notes.length ? (
        ticket.notes.map((n) => (
          <SectionText key={`${n.createdAt}-${n.by}`}>
            <strong style={{ color: P.ink }}>{n.by || 'Staff'}</strong>
            {n.createdAt && <span style={{ color: P.subtle }}>{` · ${dayjs(n.createdAt).format('MMM D, h:mm A')}`}</span>}
            <br />
            {n.text}
          </SectionText>
        ))
      ) : (
        <SectionText style={{ marginTop: 0 }}>No notes yet. Vendor updates and staff notes land here.</SectionText>
      )}
      {canManage && (
        <form onSubmit={submit} style={{ marginTop: 12, display: 'grid', gap: 8 }}>
          <TextArea aria-label="New note" placeholder="Add a note…" value={text} onChange={(e) => setText(e.target.value)} style={{ minHeight: 72 }} />
          <PrimaryBtn type="submit" disabled={!text.trim() || add.isPending} style={{ justifyContent: 'center' }}>
            <FontAwesomeIcon icon={faPaperPlane} />
            Add note
          </PrimaryBtn>
        </form>
      )}
    </Section>
  );
}

function TicketDetail({ ticket, onClose }) {
  const navigate = useNavigate();
  const { can } = useAuth();
  const canManage = can('manage_maintenance');
  const age = ageOf(ticket.opened);
  const [callId, setCallId] = useState(null);
  const remove = useDeleteTicket();

  const confirmDelete = () =>
    Modal.confirm({
      title: `Delete ${ticket.number || 'this ticket'}?`,
      content: 'The ticket and its notes are removed for good.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: () => {
        onClose();
        remove.mutate(ticket.id);
      }
    });

  return (
    <>
      <DrawerHeader>
        <DrawerHeadRow>
          <div style={{ minWidth: 0 }}>
            <DrawerTitle>{ticket.issue}</DrawerTitle>
            <DrawerSubtitle>{[ticket.number, ticket.property].filter(Boolean).join(' · ')}</DrawerSubtitle>
          </div>
          <DrawerClose type="button" onClick={onClose} aria-label="Close ticket">
            <FontAwesomeIcon icon={faXmark} />
          </DrawerClose>
        </DrawerHeadRow>
        <DrawerStats>
          <div>
            <dt>Opened</dt>
            <dd>{ticket.opened ? dayjs(ticket.opened).format('MMM D, h:mm A') : '—'}</dd>
          </div>
          {ticket.by && (
            <div>
              <dt>By</dt>
              <dd>{ticket.by}</dd>
            </div>
          )}
          <div>
            <dt>Assigned</dt>
            <dd>{ticket.assigned || 'Unassigned'}</dd>
          </div>
        </DrawerStats>
      </DrawerHeader>
      <DrawerBody>
        <PillRow>
          <StatusPill color={TONE[ticket.statusTone]}>{ticket.status}</StatusPill>
          <StatusPill color={TONE[ticket.tierTone]}>{ticket.tier}</StatusPill>
          {ticket.opened && <StatusPill color={AGE_TONE[age.tone]}>{age.text}</StatusPill>}
        </PillRow>
        <WorkOrder ticket={ticket} canManage={canManage} />
        <OwnerApproval key={ticket.id} ticket={ticket} canManage={canManage} />
        <Notes ticket={ticket} canManage={canManage} />
        {ticket.property && (
          <GhostBtn type="button" onClick={() => navigate('/properties', { state: { openListingName: ticket.property } })}>
            <FontAwesomeIcon icon={faUpRightFromSquare} />
            Open unit record
          </GhostBtn>
        )}
        {ticket.callId && can('view_calls') && (
          <GhostBtn type="button" onClick={() => setCallId(ticket.callId)}>
            <FontAwesomeIcon icon={faPhone} />
            Open call
          </GhostBtn>
        )}
        {canManage && (
          <DangerLink type="button" onClick={confirmDelete} style={{ alignSelf: 'flex-start' }}>
            Delete ticket
          </DangerLink>
        )}
      </DrawerBody>
      <CallDrawer callId={callId} onClose={() => setCallId(null)} />
    </>
  );
}

export default function TicketDrawer({ ticket, onClose }) {
  return (
    <Drawer open={Boolean(ticket)} onClose={onClose} width={480} closable={false} title={null} styles={{ body: BODY_STYLE }}>
      {ticket && <TicketDetail ticket={ticket} onClose={onClose} />}
    </Drawer>
  );
}
